import { lineBreaker } from "@/helpers/textformatting";
import {
  getDteTemplateLabel,
  isDteTemplateFieldVisible,
  safeResolveDteTemplateSettings,
} from "@/helpers/dteTemplateSettings";

import {
  asNumber,
  buildFileBaseName,
  buildMhQrUrl,
  calculateDueDate,
  cleanAssetUrl,
  cleanText,
  formatDocumentTypeLabel,
  formatInvoiceTypeLabel,
  formatModelLabel,
  formatMoney,
  formatNit,
  formatNrc,
  formatPlainMoney,
  formatTransmissionLabel,
  getStoredUserTheme,
  htmlToText,
} from "./dtePrintFormatters";

function buildStatusBadges(header, hideQr) {
  const badges = [];

  if (hideQr) {
    badges.push({
      label: "Documento sin validez legal",
      tone: "danger",
    });
  }

  if (header.FeMhEstado === "RECHAZADO") {
    badges.push({
      label: "Documento rechazado por Hacienda",
      tone: "danger",
    });
  }

  if (header.FeMhEstado === "INVALIDADO") {
    badges.push({
      label: "Documento invalidado por Hacienda",
      tone: "danger",
    });
  }

  return badges;
}

function shouldShowTemplateField(templateSettings, section, key, fallback = true) {
  return isDteTemplateFieldVisible(templateSettings, section, key, fallback);
}

function getTemplateLabel(templateSettings, key, fallback) {
  return getDteTemplateLabel(templateSettings, key, fallback);
}

function isPlainDuiValue(value) {
  const normalized = cleanText(value, "").trim();
  return /^\d{9}$/.test(normalized);
}

function resolveReceiverDocument(rawData) {
  const header = rawData.header || {};
  const customerInfo = header.customerInfo || {};
  const rawCandidates = [
    cleanText(customerInfo.dui, ""),
    cleanText(header.ReceptorNumDocumento, ""),
    cleanText(header.InvoiceDUI, ""),
  ].filter(Boolean);

  const documentNumber = rawCandidates.find((value) => isPlainDuiValue(value)) || "";

  return {
    documentLabel: documentNumber
      ? formatDocumentTypeLabel(customerInfo.ID_DocumentType || "13") || "DUI"
      : "",
    documentNumber,
  };
}

function resolvePaymentStatusLabel(value) {
  const normalized = cleanText(value, "---");

  if (normalized.toLowerCase() === "cancelada") {
    return "Cancelada / Contado";
  }

  return normalized;
}

function buildIssuer(rawData, templateSettings) {
  const header = rawData.header || {};
  const branch = rawData.branch || {};
  const user = rawData.user || {};
  const showPhone = shouldShowTemplateField(templateSettings, "issuer", "phone");

  return {
    businessName: cleanText(user.business, ""),
    tradeName: cleanText(branch.BusinessBillingName, ""),
    activity: shouldShowTemplateField(templateSettings, "issuer", "businessLine")
      ? cleanText(header.EmisorDescActividad, "")
      : "",
    address: shouldShowTemplateField(templateSettings, "issuer", "address")
      ? cleanText(branch.BranchLegalAddress, "")
      : "",
    phone: showPhone ? cleanText(branch.BranchPhone, "") : "",
    mobile: showPhone ? cleanText(branch.BranchMobile, "") : "",
    email: shouldShowTemplateField(templateSettings, "issuer", "email")
      ? cleanText(branch.BranchEmail, "")
      : "",
    phoneLabel: getTemplateLabel(templateSettings, "phone", "Tel."),
    emailLabel: getTemplateLabel(templateSettings, "email", "Correo"),
    nit: formatNit(branch.BusinessBillingNIT),
    nrc: formatNrc(branch.BusinessBillingNRC),
    logoUrl: cleanAssetUrl(branch.BranchLogoPath),
    bannerUrl: cleanAssetUrl(branch.BranchBannerPath),
    billedInfoHtml: cleanText(branch.BranchBilledInfo, ""),
    billedInfoText: htmlToText(branch.BranchBilledInfo),
  };
}

function buildReceiver(rawData) {
  const header = rawData.header || {};
  const dteJson = rawData.dteJson || {};
  const customerInfo = header.customerInfo || {};
  const { documentLabel, documentNumber } = resolveReceiverDocument(rawData);

  return {
    code: cleanText(header.ID_Customer, ""),
    name: cleanText(header.InvoiceName, ""),
    sellerCode: cleanText(header.ID_Seller, ""),
    sellerName: cleanText(header.FullNameSeller, ""),
    address: cleanText(header.InvoiceAddress, ""),
    addressSecondary: cleanText(header.InvoiceAddress2, ""),
    dui: cleanText(header.InvoiceDUI, ""),
    nit: cleanText(header.InvoiceNIT, ""),
    nrc: cleanText(header.InvoiceNRC, ""),
    email: cleanText(header.customerEmail || dteJson.ReceptorCorreo, ""),
    entity: cleanText(header.InvoiceEntity, ""),
    zone: cleanText(header.ZoneClient, ""),
    activity: cleanText(dteJson.ReceptorDescActividad || customerInfo.descActividad || header.InvoiceSector, ""),
    phone: cleanText(
      header.ClienteTelefonoFijoDeContacto ||
        header.ClienteTelefonoMovilDeContacto ||
        header.ReceptorTelefono,
      ""
    ),
    contactName: cleanText(customerInfo.BusinessContactName || header.FullNameCustomer, ""),
    documentLabel,
    documentNumber,
  };
}

function buildRelatedDocuments(rawData) {
  const ndOrNc = rawData.ndornc || rawData.header?.ndornc || null;
  const relatedSource =
    rawData.docRelated ||
    (ndOrNc ? [ndOrNc] : null) ||
    rawData.relatedDocuments ||
    rawData.header?.docRelated ||
    rawData.header?.JsonMh?.dteJson?.documentoRelacionado ||
    rawData.header?.jsonMH?.documentoRelacionado ||
    rawData.dteJson?.documentoRelacionado ||
    [];

  return (Array.isArray(relatedSource) ? relatedSource : []).map((item) => ({
    typeLabel: item.TipoDocumentoRelacionado === "03"
      ? "Comprobante de Credito Fiscal"
      : item.tipoDocumento === "03"
        ? "Comprobante de Credito Fiscal"
      : cleanText(item.TipoDocumentoRelacionado, "---"),
    number: cleanText(item.NumeroDocumentoRelacionado || item.numeroDocumento || item.codigoGeneracion || "---", "---"),
    date: cleanText(item.FechaEmisionRelacionado || item.fechaEmision || "---", "---"),
  }));
}

function buildAssociatedDocuments(rawData) {
  const dteJson = rawData.dteJson || {};
  const documents = [];

  if (dteJson.OtrosDocumentosCodDocAsociado || dteJson.OtrosDocumentosDescDocumento || dteJson.OtrosDocumentosDetalleDocumento) {
    documents.push({
      typeLabel: cleanText(dteJson.OtrosDocumentosCodDocAsociado, "---"),
      number: cleanText(dteJson.OtrosDocumentosDescDocumento, "---"),
      date: cleanText(dteJson.OtrosDocumentosDetalleDocumento, "---"),
    });
  }

  return documents;
}

function buildThirdPartySale(rawData) {
  const header = rawData.header || {};
  const dteJson = rawData.dteJson || {};
  const nit = dteJson.VentaTerceroNit || header.VentaTerceroNit;
  const name = dteJson.VentaTerceroNombre || header.VentaTerceroNombre;

  if (!nit && !name) {
    return null;
  }

  return {
    nit: cleanText(nit, "---"),
    name: cleanText(name, "---"),
  };
}

function buildItems(rawData) {
  return (rawData.body || []).map((item, index) => {
    const description = cleanText(item.ItemDescription, "");
    const [, lineCount = 1] = lineBreaker(description) || [];
    const estimatedLines = Math.max(1, Number(lineCount) || 1);

    return {
      index: item.ItemNoItem || index + 1,
      code: cleanText(item.ID_Item, ""),
      barcode: cleanText(item.PrincipalBarCode, ""),
      unit: cleanText(item.ItemMeasureUnit, ""),
      quantity: formatPlainMoney(item.ItemQuantity, 2),
      description,
      unitPrice: asNumber(item.ItemUnitPrice),
      discount: asNumber(item.ItemDiscount),
      exempt: asNumber(item.ItemExenta),
      nonSubject: asNumber(item.ItemNoSuj),
      nonTaxed: asNumber(item.ItemNoGravada),
      itemType: cleanText(item.ItemType || item.TipoItem || "3", "3"),
      taxed: asNumber(item.ItemGravada),
      total:
        asNumber(item.ItemGravada) +
        asNumber(item.ItemExenta) +
        asNumber(item.ItemNoSuj) +
        asNumber(item.ItemNoGravada),
      rowUnits: Math.max(0.88, 0.88 + ((estimatedLines - 1) * 0.82)),
    };
  });
}

function addTotalRow(
  rows,
  templateSettings,
  key,
  label,
  value,
  currency,
  options = {}
) {
  const numericValue = asNumber(value);
  const shouldRender = options.always || numericValue > 0;

  if (!shouldRender) {
    return;
  }

  const visibilityMap = {
    discounts: "discounts",
    exempt: "exempt",
    nonSubject: "nonSubject",
    nonTaxable: "nonTaxable",
    retention: "retention",
    perception: "perception",
    fovial: "fovial",
    cotrans: "cotrans",
  };

  const visibilityKey = visibilityMap[key];
  if (visibilityKey && !shouldShowTemplateField(templateSettings, "totals", visibilityKey)) {
    return;
  }

  rows.push({
    key,
    label,
    displayLabel: getTemplateLabel(templateSettings, key, label),
    value: formatMoney(numericValue, currency),
    emphasis: Boolean(options.emphasis),
  });
}

function buildTotalRows(header, templateSettings, rawData) {
  const currency = cleanText(header.InvoiceCurrency, "$");
  const invoiceType = cleanText(header.InvoiceType, "");
  const rows = [];

  if (invoiceType === "CDD") {
    addTotalRow(rows, templateSettings, "total", "Total de la donacion", header.InvoiceTotalPagar, currency, { emphasis: true, always: true });
    return rows;
  }

  if (invoiceType === "FEX") {
    addTotalRow(rows, templateSettings, "subtotal", "Operaciones afectas", header.InvoiceSubTotalVentas, currency, { always: true });
    addTotalRow(rows, templateSettings, "discounts", "Descuentos", header.InvoiceTotalDescu, currency);
    addTotalRow(rows, templateSettings, "freight", "Flete", header.Flete, currency);
    addTotalRow(rows, templateSettings, "insurance", "Seguro", header.Seguro, currency);
    addTotalRow(rows, templateSettings, "nonTaxable", "Otros montos no afectos", header.InvoiceTotalNoGravado, currency);
    addTotalRow(rows, templateSettings, "total", "Monto total de la operacion", header.InvoiceTotalPagar, currency, { emphasis: true, always: true });
    return rows;
  }

  addTotalRow(rows, templateSettings, "subtotal", "Sub total de ventas", header.InvoiceSubTotalVentas || header.InvoiceTotalGravada, currency, { always: true });
  addTotalRow(rows, templateSettings, "discounts", "Descuentos", header.InvoiceTotalDescu, currency);
  addTotalRow(rows, templateSettings, "exempt", "Ventas exentas", header.InvoiceTotalExenta, currency);
  addTotalRow(rows, templateSettings, "nonSubject", "Ventas no sujetas", header.InvoiceTotalNoSuj, currency);
  addTotalRow(rows, templateSettings, "nonTaxable", "Otros montos no afectos", header.InvoiceTotalNoGravado, currency);
  const relatedDocuments = [].concat(
    rawData.docRelated || [],
    header.docRelated || [],
    rawData.ndornc || header.ndornc || [],
    header.JsonMh?.dteJson?.documentoRelacionado || [],
    header.jsonMH?.documentoRelacionado || [],
    rawData.dteJson?.documentoRelacionado || []
  );
  const isFerFromFns = invoiceType === "FER" && relatedDocuments.some((document) =>
    ["14", "FNS"].includes(String(document?.TipoDocumentoRelacionado || document?.tipoDocumento))
  );

  if (!["FCF", "FEX", "FNS"].includes(invoiceType)) {
    if (!isFerFromFns) {
      addTotalRow(rows, templateSettings, "iva", "IVA", header.InvoiceTotalIva, currency);
    }
    addTotalRow(rows, templateSettings, "retention", "IVA retenido", header.InvoiceIvaRete1, currency);
    addTotalRow(rows, templateSettings, "perception", "IVA percibido", header.InvoiceIvaPerce1, currency);
  }
  addTotalRow(rows, templateSettings, "retention", "Renta retenida", header.InvoiceReteRenta, currency, { always: isFerFromFns });
  addTotalRow(rows, templateSettings, "fovial", "Fovial", header.Fovial, currency);
  addTotalRow(rows, templateSettings, "cotrans", "Cotrans", header.Cotrans, currency);
  addTotalRow(rows, templateSettings, "total", "Total", header.InvoiceTotalPagar, currency, { emphasis: true, always: true });

  return rows;
}

function buildMeta(rawData) {
  const header = rawData.header || {};
  const dteJson = rawData.dteJson || {};
  const invoiceType = cleanText(header.InvoiceType, "");

  return {
    invoiceId: cleanText(header.ID_Invoice, "---"),
    controlNumber: invoiceType === "FER" ? "" : cleanText(header.NumeroControl, "---"),
    generationCode: cleanText(header.CodigoGeneracion, "---"),
    receiptSeal: cleanText(header.SelloRecepcion, "---"),
    invoiceType,
    invoiceTypeLabel: formatInvoiceTypeLabel(invoiceType),
    issueDate: cleanText(header.InvoiceDate, "---"),
    dueDate: calculateDueDate(header.InvoiceDate, header.InvoiceCreditDay),
    currency: cleanText(header.InvoiceCurrency, "$"),
    modelLabel: formatModelLabel(dteJson.IdentificacionTipoModelo),
    transmissionLabel: formatTransmissionLabel(dteJson.IdentificacionTipoOperacion),
    paymentStatus: resolvePaymentStatusLabel(header.InvoiceStatusDescription),
    correlative: cleanText(header.Correlative_ID_Invoice, ""),
    notes: htmlToText(header.InvoiceInternalNote),
    amountInWords: cleanText(header.InvoiceTotalLetras, ""),
  };
}

export function buildJsonPayload(rawData) {
  const header = rawData.header || {};
  const dteJson = (header.JsonMh && header.JsonMh.dteJson) || (rawData.dteJson && rawData.dteJson.JsonMh && rawData.dteJson.JsonMh.dteJson) || rawData.dteJson || {};
  let selloRecibido = "";

  try {
    selloRecibido = header.ResponseMhJson ? JSON.parse(header.ResponseMhJson)?.selloRecibido || "" : "";
  } catch (error) {
    selloRecibido = "";
  }

  const firmaElectronica = header.JsonMhSigned?.documento || "";
  const payload = { ...dteJson, firmaElectronica, selloRecibido };

  delete payload.passwordPri;
  return payload;
}

export function assignCuerpoDocumento(rawData) {
  const header = rawData.header || {};
  if (!header.jsonMH) {
    return;
  }

  header.jsonMH.cuerpoDocumento = (rawData.body || []).map((item, index) => ({
    descripcion: item.ItemDescription,
    montoDescu: item.ItemDiscount,
    codigo: item.ID_Item,
    ventaGravada: item.ItemGravada,
    ivaItem: item.ItemIva,
    ventaNoSuj: item.ItemNoSuj,
    ventaExenta: item.ItemExenta,
    numItem: index + 1,
    noGravado: item.ItemNoGravada,
    psv: item.ItemPsv,
    uniMedida: item.ItemMeasureUnit,
    cantidad: item.ItemQuantity,
    precioUni: item.ItemUnitPrice,
  }));
}

export function normalizeDtePrintData(dataProps, options = {}) {
  const rawData = (dataProps && dataProps.data) || {};
  const header = rawData.header || {};
  const templateLayoutKey = options.templateLayoutKey || "estandar";
  const templateSettings = safeResolveDteTemplateSettings(
    options.templateSettings || {},
    templateLayoutKey
  );
  const theme = {
    ...getStoredUserTheme(),
    ...(options.theme || {}),
  };
  const hideAdvertising = Boolean(
    templateSettings &&
    templateSettings.theme &&
    templateSettings.theme.hideAdvertising
  );
  const hideAdvertisingOnPrint = Boolean(
    options.forPrint &&
    templateSettings &&
    templateSettings.theme &&
    templateSettings.theme.hideAdvertisingOnPrint
  );

  const meta = buildMeta(rawData);
  const items = buildItems(rawData);
  const issuer = buildIssuer(rawData, templateSettings);
  const showFullPageBanner = Boolean(
    issuer.bannerUrl &&
    templateSettings.theme &&
    templateSettings.theme.bannerFullPage &&
    !hideAdvertising &&
    !hideAdvertisingOnPrint
  );
  const showInlineBanner = Boolean(
    issuer.bannerUrl &&
    !(templateSettings.theme && templateSettings.theme.bannerFullPage) &&
    !hideAdvertising &&
    !hideAdvertisingOnPrint
  );

  return {
    rawData,
    meta,
    issuer,
    receiver: buildReceiver(rawData),
    appendices: Array.isArray(header.apendice)
      ? header.apendice
      : Array.isArray(rawData.apendice)
        ? rawData.apendice
        : [],
    relatedDocuments: buildRelatedDocuments(rawData),
    associatedDocuments: buildAssociatedDocuments(rawData),
    thirdPartySale: buildThirdPartySale(rawData),
    items,
    totalRows: buildTotalRows(header, templateSettings, rawData),
    notes: meta.notes,
    showQrSection: !options.hideQr,
    qrUrl: buildMhQrUrl(rawData),
    badges: buildStatusBadges(header, options.hideQr),
    fileBaseName: buildFileBaseName(header),
    jsonPayload: buildJsonPayload(rawData),
    amountInWords: meta.amountInWords,
    footerContacts: {
      issuerName: cleanText(header.ExtensionNombEntrega || header.FullName, ""),
      issuerDocument: cleanText(header.ExtensionDocuEntrega || header.EmisorUserDui, ""),
      issuerPhone: cleanText(header.InvoiceExtTelEntrega || header.EmisorUserMobile, ""),
      receiverName: cleanText(header.ExtensionNombRecibe || rawData.header?.customerInfo?.BusinessContactName || header.ReceptorNombre, ""),
      receiverDocument: cleanText(
        header.ExtensionDocuRecibe ||
        rawData.header?.customerInfo?.dui ||
        header.ReceptorNumDocumento ||
        header.ReceptorNit ||
        header.ReceptorNrc,
        ""
      ),
      receiverPhone: cleanText(
        header.InvoiceExtTelRecibe ||
        rawData.header?.customerInfo?.BusinessContactPhone ||
        header.ClienteTelefonoFijoDeContacto ||
        header.ReceptorTelefono,
        ""
      ),
    },
    showLargeAmountResponsibles: header.ShowResponsabilitiesInInvoice || asNumber(header.InvoiceTotalPagar) >= 25000,
    hideAdvertising,
    hideAdvertisingOnPrint,
    showFullPageBanner,
    showInlineBanner,
    theme,
    templateSettings,
  };
}
